// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getProfile } from "../redux/slices/authSlice";
import { getWallet } from "../redux/slices/walletSlice";
import {
  getTransactions,
  getTransactionStatistics,
} from "../redux/slices/transactionSlice";
import { getKycStatus } from "../redux/slices/kycSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  TrendingUp,
  Wallet as WalletIcon,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  FileText,
  AlertCircle,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatFiat } from "../utils/currency";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { wallet } = useSelector((state: RootState) => state.wallet);
  const { transactions } = useSelector(
    (state: RootState) => state.transactions
  );
  const { kyc } = useSelector((state: RootState) => state.kyc);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(getProfile() as any),
          dispatch(getWallet() as any),
          dispatch(getTransactions() as any),
          dispatch(getTransactionStatistics() as any),
          dispatch(getKycStatus() as any),
        ]);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]);

  const getVerificationStatus = (level: number) => {
    switch (level) {
      case 0:
        return {
          text: "Unverified",
          color: "text-red-500",
          icon: <XCircle className="h-4 w-4" />,
        };
      case 1:
        return {
          text: "Email Verified",
          color: "text-yellow-500",
          icon: <Clock className="h-4 w-4" />,
        };
      case 2:
        return {
          text: "KYC Submitted",
          color: "text-blue-500",
          icon: <FileText className="h-4 w-4" />,
        };
      case 3:
        return {
          text: "KYC Approved",
          color: "text-green-500",
          icon: <CheckCircle className="h-4 w-4" />,
        };
      default:
        return {
          text: "Unknown",
          color: "text-gray-500",
          icon: <AlertCircle className="h-4 w-4" />,
        };
    }
  };

  const getKycStatusText = () => {
    if (!kyc || !kyc.status) return "Not Submitted";
    return kyc.status.charAt(0).toUpperCase() + kyc.status.slice(1);
  };

  // Get recent transactions (deposits and withdrawals)
  const recentTransactions = transactions.slice(0, 5);

  // Calculate stats
  const totalDeposits = transactions
    .filter((t) => t.type === "deposit" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalWithdrawals = transactions
    .filter((t) => t.type === "withdrawal" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);

  const pendingTransactions = transactions.filter(
    (t) => t.status === "pending"
  ).length;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Welcome Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <h1 className={GlobalStyles.heading1}>
            Welcome back, {user?.first_name}!
          </h1>
          <p className={GlobalStyles.bodyText + " mt-2"}>
            Here's what's happening with your account today.
          </p>
        </div>

        {/* Stats Grid */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          {/* Balance Card */}
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <WalletIcon className="h-6 w-6 text-primary" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
              >
                Active
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Balance
            </p>
            <p className={`${GlobalStyles.heading2} mt-1`}>
              {formatFiat(user?.balance || 0)}
            </p>
            <div className="flex items-center mt-4">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600 dark:text-green-400">
                +2.5% from last month
              </span>
            </div>
          </div>

          {/* Deposits Card */}
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <ArrowDownRight className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Deposits
            </p>
            <p className={`${GlobalStyles.heading2} mt-1`}>
              {formatFiat(totalDeposits)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {transactions.filter((t) => t.type === "deposit").length}{" "}
              transactions
            </p>
          </div>

          {/* Withdrawals Card */}
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20">
                <ArrowUpRight className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Withdrawals
            </p>
            <p className={`${GlobalStyles.heading2} mt-1`}>
              {formatFiat(totalWithdrawals)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {transactions.filter((t) => t.type === "withdrawal").length}{" "}
              transactions
            </p>
          </div>

          {/* Verification Card */}
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Verification Level
            </p>
            <div className="flex items-center mt-1">
              {getVerificationStatus(user?.verification_level || 0).icon}
              <p
                className={`text-xl font-bold ml-2 ${
                  getVerificationStatus(user?.verification_level || 0).color
                }`}
              >
                Level {user?.verification_level}
              </p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {getVerificationStatus(user?.verification_level || 0).text}
            </p>
          </div>
        </div>

        {/* Recent Activity & Quick Actions */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          {/* Recent Transactions */}
          <div className={`${GlobalStyles.card} lg:col-span-2`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={GlobalStyles.heading3}>Recent Transactions</h3>
              <Link
                to="/transactions"
                className="text-sm text-primary hover:text-primary/80"
              >
                View All
              </Link>
            </div>

            <div className="space-y-3">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center">
                      <div
                        className={`p-2 rounded-lg mr-3 ${
                          transaction.type === "deposit"
                            ? "bg-green-100 dark:bg-green-900/20"
                            : transaction.type === "withdrawal"
                            ? "bg-red-100 dark:bg-red-900/20"
                            : "bg-purple-100 dark:bg-purple-900/20"
                        }`}
                      >
                        {transaction.type === "deposit" ? (
                          <ArrowDownRight className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : transaction.type === "withdrawal" ? (
                          <ArrowUpRight className="h-4 w-4 text-red-600 dark:text-red-400" />
                        ) : (
                          <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {transaction.type}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            transaction.created_at
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-medium ${
                          transaction.type === "deposit"
                            ? "text-green-600 dark:text-green-400"
                            : transaction.type === "withdrawal"
                            ? "text-red-600 dark:text-red-400"
                            : "text-purple-600 dark:text-purple-400"
                        }`}
                      >
                        {transaction.type === "deposit" ? "+" : "-"}
                        {formatFiat(transaction.amount)}
                      </p>
                      <span
                        className={`${GlobalStyles.badge} ${
                          transaction.status === "confirmed"
                            ? GlobalStyles.badgeSuccess
                            : transaction.status === "pending"
                            ? GlobalStyles.badgeWarning
                            : GlobalStyles.badgeError
                        } text-xs mt-1`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No transactions yet
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className={GlobalStyles.card}>
            <h3 className={`${GlobalStyles.heading3} mb-6`}>Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/deposit"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
              >
                <ArrowDownRight className="h-4 w-4 mr-2" />
                Make Deposit
              </Link>
              <Link
                to="/withdraw"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
              >
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Request Withdrawal
              </Link>
              {user?.verification_level === 1 && !kyc && (
                <Link
                  to="/profile"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Complete KYC
                </Link>
              )}
              <Link
                to="/recovery"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
              >
                <Shield className="h-4 w-4 mr-2" />
                Recovery Services
              </Link>
              <Link
                to="/wallet"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} w-full justify-start`}
              >
                <WalletIcon className="h-4 w-4 mr-2" />
                Manage Wallet
              </Link>
            </div>
          </div>
        </div>

        {/* Wallet & KYC Status */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6`}>
          {/* Wallet Status */}
          <div className={GlobalStyles.card}>
            <h3 className={`${GlobalStyles.heading3} mb-6`}>Wallet Status</h3>
            {wallet ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {wallet.wallet_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connected on{" "}
                      {new Date(wallet.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
                  >
                    Connected
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Transactions
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      {transactions.length}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Network
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      Multi-chain
                    </p>
                  </div>
                </div>
                <Link
                  to="/wallet"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full`}
                >
                  <WalletIcon className="h-4 w-4 mr-2" />
                  Manage Wallet
                </Link>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <WalletIcon className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Connect your wallet to start transactions
                </p>
                <Link
                  to="/wallet"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                >
                  Connect Wallet
                </Link>
              </div>
            )}
          </div>

          {/* KYC Status */}
          <div className={GlobalStyles.card}>
            <h3 className={`${GlobalStyles.heading3} mb-6`}>KYC Status</h3>
            {kyc ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Current Status
                  </span>
                  <span
                    className={`${GlobalStyles.badge} ${
                      kyc.status === "approved"
                        ? GlobalStyles.badgeSuccess
                        : kyc.status === "pending"
                        ? GlobalStyles.badgeWarning
                        : kyc.status === "rejected"
                        ? GlobalStyles.badgeError
                        : ""
                    }`}
                  >
                    {getKycStatusText()}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                      Document Type:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                      {kyc.document_type?.replace("_", " ") || "N/A"}
                    </span>
                  </div>
                  {kyc.submitted_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Submitted:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(kyc.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {kyc.verified_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Verified:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {new Date(kyc.verified_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                {kyc.status === "pending" && (
                  <div
                    className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4`}
                  >
                    <Clock className="h-5 w-5 flex-shrink-0" />
                    <p className="text-sm">
                      Your KYC verification is under review. This usually takes
                      24-48 hours.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <Shield className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Complete KYC verification to unlock all platform features
                </p>
                {user?.verification_level === 1 && (
                  <Link
                    to="/profile"
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium}`}
                  >
                    Start KYC Verification
                  </Link>
                )}
                {user?.verification_level === 0 && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    Please verify your email first
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Additional Stats */}
        <div className={`${GlobalStyles.gridCols3} gap-6 mt-8`}>
          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Active Today
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {
                    transactions.filter(
                      (t) =>
                        new Date(t.created_at).toDateString() ===
                        new Date().toDateString()
                    ).length
                  }
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pending
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {pendingTransactions}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/20">
                <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className={GlobalStyles.card}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {transactions.length > 0
                    ? `${Math.round(
                        (transactions.filter((t) => t.status === "confirmed")
                          .length /
                          transactions.length) *
                          100
                      )}%`
                    : "0%"}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
